import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  PaperPlaneTilt, Plus, Users, CheckCircle, Clock, Warning,
  ArrowClockwise, Trash, UserPlus, FileText, GraduationCap, X,
  Eye, DownloadSimple, Copy, Link, WarningCircle, PencilSimple
} from "@phosphor-icons/react";

const ROLE_OPTIONS = [
  { value: "opportunity_finder", label: "Opportunity Finder" },
  { value: "candidate_finder", label: "Candidate Finder" },
  { value: "senior_recruitment_partner", label: "Senior Recruitment Partner" },
  { value: "team_leader", label: "Team Leader" },
];

const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-800",
  accepted: "bg-green-100 text-green-800",
  expired: "bg-red-100 text-red-800",
  revoked: "bg-gray-100 text-gray-800",
  direct: "bg-blue-100 text-blue-800",
};

const ONBOARDING_COLORS = {
  invited: "bg-amber-100 text-amber-800",
  registered: "bg-blue-100 text-blue-800",
  id_rtw_uploaded: "bg-cyan-100 text-cyan-800",
  agreements_signed: "bg-purple-100 text-purple-800",
  academy_complete: "bg-green-100 text-green-800",
  active: "bg-green-100 text-green-800",
};

const ONBOARDING_STATUSES = [
  { value: "invited", label: "Invited" },
  { value: "registered", label: "Registered" },
  { value: "id_rtw_uploaded", label: "ID/RTW Uploaded" },
  { value: "agreements_signed", label: "Agreements Signed" },
  { value: "academy_complete", label: "Academy Complete" },
  { value: "active", label: "Active" },
];

export default function OnboardingAdminPage() {
  const [dashboard, setDashboard] = useState({ items: [], stats: {} });
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [inviteForm, setInviteForm] = useState({ name: "", email: "", role: "opportunity_finder", team_id: "" });
  const [bulkText, setBulkText] = useState("");
  const [bulkRole, setBulkRole] = useState("opportunity_finder");
  const [bulkTeam, setBulkTeam] = useState("");
  const [sending, setSending] = useState(false);
  const [teams, setTeams] = useState([]);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleteMode, setDeleteMode] = useState("deactivate");

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, teamsRes] = await Promise.all([
        api.get("/onboarding/dashboard"),
        api.get("/teams/dropdown"),
      ]);
      setDashboard(dashRes.data);
      setTeams(teamsRes.data || []);
    } catch (err) {
      console.error("Failed to fetch onboarding dashboard", err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDashboard(); }, [fetchDashboard]);

  const [inviteResult, setInviteResult] = useState(null);

  async function handleSingleInvite(e) {
    e.preventDefault();
    if (!inviteForm.name || !inviteForm.email) { toast.error("Name and email required"); return; }
    if (!inviteForm.team_id) { toast.error("Please assign a team"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/onboarding/invite", inviteForm);
      if (data.email_sent) {
        toast.success(`Invitation emailed to ${inviteForm.email}`);
        setShowInvite(false);
        setInviteForm({ name: "", email: "", role: "opportunity_finder", team_id: "" });
      } else {
        setInviteResult(data);
        toast.warning("Invitation created but email could not be delivered. Copy the link below.");
      }
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invite");
    } finally { setSending(false); }
  }

  const [bulkResult, setBulkResult] = useState(null);

  async function handleBulkInvite() {
    const lines = bulkText.split("\n").filter(l => l.trim());
    const invites = lines.map(line => {
      const parts = line.split(",").map(s => s.trim());
      return { name: parts[0] || "", email: parts[1] || "", role: parts[2] || bulkRole };
    }).filter(i => i.name && i.email);
    if (!invites.length) { toast.error("No valid entries. Format: Name, Email"); return; }
    if (!bulkTeam) { toast.error("Select a default team"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/onboarding/invite-bulk", { invites, team_id: bulkTeam });
      if (data.domain_verification_needed) {
        setBulkResult(data);
        toast.warning(data.message);
      } else {
        toast.success(data.message);
        setShowBulk(false);
        setBulkText("");
      }
      fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Bulk invite failed"); }
    finally { setSending(false); }
  }

  async function handleResend(invitationId) {
    try {
      const { data } = await api.post(`/onboarding/resend-invite/${invitationId}`);
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to resend"); }
  }

  async function handleRevoke(invitationId) {
    try {
      await api.delete(`/onboarding/invite/${invitationId}`);
      toast.success("Invitation revoked");
      fetchDashboard();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to revoke"); }
  }

  function openEdit(item) {
    const teamId = item.team_id || teams.find(t => t.name === item.team_name)?.id || "";
    setEditForm({
      name: item.name,
      email: item.email,
      role: item.role,
      team_id: teamId,
      onboarding_status: item.onboarding_status || "registered",
    });
    setEditItem(item);
  }

  async function saveEdit() {
    if (!editItem?.user_id) { toast.error("Cannot edit: user not registered yet"); return; }
    try {
      const teamName = teams.find(t => t.id === editForm.team_id)?.name || "";
      await api.put(`/users/${editItem.user_id}`, {
        name: editForm.name,
        email: editForm.email,
        role: editForm.role,
        team_name: teamName,
        onboarding_status: editForm.onboarding_status,
      });
      toast.success("User updated");
      setEditItem(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  }

  async function confirmDelete() {
    if (!deleteItem?.user_id) { toast.error("Cannot delete: user not registered"); return; }
    try {
      await api.delete(`/users/${deleteItem.user_id}?mode=${deleteMode}`);
      toast.success(deleteMode === "permanent" ? "User permanently deleted" : "User deactivated");
      setDeleteItem(null);
      fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  }

  const stats = dashboard.stats || {};

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#0F172A]" data-testid="onboarding-admin-title">Onboarding Dashboard</h1>
          <p className="text-[#6B7280] text-sm mt-1">Manage invitations, edit users, and track onboarding progress</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={() => setShowBulk(true)} data-testid="bulk-invite-btn">
            <Users size={16} /> Bulk Invite
          </Button>
          <Button className="bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] gap-2 font-semibold" onClick={() => setShowInvite(true)} data-testid="single-invite-btn">
            <Plus size={16} /> Invite User
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total Invited", value: stats.total_invited || 0, icon: PaperPlaneTilt, color: "text-blue-600", bg: "bg-blue-50" },
          { label: "Pending", value: stats.pending || 0, icon: Clock, color: "text-amber-600", bg: "bg-amber-50" },
          { label: "Accepted", value: stats.accepted || 0, icon: CheckCircle, color: "text-green-600", bg: "bg-green-50" },
          { label: "Registered Users", value: stats.registered_users || 0, icon: Users, color: "text-purple-600", bg: "bg-purple-50" },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} rounded-xl p-4 border`}>
            <s.icon size={22} className={s.color} />
            <div className="text-2xl font-bold text-[#0F172A] mt-2">{s.value}</div>
            <div className="text-xs text-[#6B7280] font-medium">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Agreement Preview */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-[#0F172A] flex items-center gap-2">
            <FileText size={18} className="text-[#C5A059]" /> Agreement Templates
          </h3>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[{ type: "nda", label: "NDA" }, { type: "partner", label: "Partner" }, { type: "senior_partner", label: "Senior Partner" }, { type: "team_leader", label: "Team Leader" }].map(ag => (
            <button key={ag.type} className="flex items-center gap-2 px-4 py-3 rounded-lg border border-[#E5E7EB] hover:border-[#C5A059] hover:bg-[#FFFBEB] transition-all text-left"
              onClick={async () => { try { const r = await api.get(`/agreements/preview/${ag.type}/pdf`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" })); window.open(url, "_blank"); } catch { toast.error("Preview failed"); } }}>
              <Eye size={16} className="text-[#C5A059] shrink-0" />
              <span className="text-sm font-medium text-[#374151]">{ag.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* User Table */}
      <div className="bg-white rounded-xl border border-[#E5E7EB] overflow-hidden">
        <div className="px-5 py-4 border-b border-[#E5E7EB]">
          <h3 className="font-bold text-[#0F172A]">All Users & Invitations</h3>
        </div>
        {loading ? (
          <div className="p-8 text-center text-[#6B7280]">Loading...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="onboarding-table">
              <thead className="bg-[#F9FAFB] text-[#374151]">
                <tr>
                  <th className="text-left px-4 py-3 font-medium">Name</th>
                  <th className="text-left px-4 py-3 font-medium">Email</th>
                  <th className="text-left px-4 py-3 font-medium">Role</th>
                  <th className="text-left px-4 py-3 font-medium">Team</th>
                  <th className="text-left px-4 py-3 font-medium">Invite</th>
                  <th className="text-left px-4 py-3 font-medium">Onboarding</th>
                  <th className="text-left px-4 py-3 font-medium">Agreements</th>
                  <th className="text-left px-4 py-3 font-medium">Academy</th>
                  <th className="text-left px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1F5F9]">
                {(dashboard.items || []).map((item, idx) => {
                  const rl = { opportunity_finder: "RP", candidate_finder: "CF", team_leader: "TL", senior_recruitment_partner: "SRP" }[item.role] || item.role;
                  return (
                    <tr key={item.email + idx} className="hover:bg-[#F9FAFB]" data-testid={`user-row-${item.email}`}>
                      <td className="px-4 py-3 font-medium text-[#0F172A]">{item.name}</td>
                      <td className="px-4 py-3 text-[#6B7280] text-xs">{item.email}</td>
                      <td className="px-4 py-3"><span className="text-xs font-bold px-2 py-0.5 rounded bg-[#F1F5F9] text-[#374151]">{rl}</span></td>
                      <td className="px-4 py-3 text-xs text-[#374151]">{item.team_name || <span className="text-[#9CA3AF]">—</span>}</td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${STATUS_COLORS[item.invite_status] || "bg-gray-100 text-gray-800"}`}>
                          {item.invite_status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded ${ONBOARDING_COLORS[item.onboarding_status] || "bg-gray-100 text-gray-800"}`}>
                          {(item.onboarding_status || "").replace(/_/g, " ")}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.registered && item.agreements_signed !== undefined ? (
                          <span className="flex items-center gap-1 text-xs">
                            <FileText size={14} className={item.agreements_pending > 0 ? "text-amber-500" : "text-green-500"} />
                            {item.agreements_signed}/{(item.agreements_signed || 0) + (item.agreements_pending || 0)}
                          </span>
                        ) : <span className="text-xs text-[#9CA3AF]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {item.registered && item.academy_total !== undefined ? (
                          <span className="flex items-center gap-1 text-xs">
                            <GraduationCap size={14} className={item.academy_completed >= item.academy_total && item.academy_total > 0 ? "text-green-500" : "text-amber-500"} />
                            {item.academy_completed || 0}/{item.academy_total || 0}
                          </span>
                        ) : <span className="text-xs text-[#9CA3AF]">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1">
                          {/* Edit & Delete for registered users */}
                          {item.registered && item.user_id && (
                            <>
                              <button
                                className="p-1.5 rounded hover:bg-blue-50 text-[#6B7280] hover:text-blue-600"
                                title="Edit user"
                                onClick={() => openEdit(item)}
                                data-testid={`edit-${item.email}`}
                              >
                                <PencilSimple size={14} />
                              </button>
                              <button
                                className="p-1.5 rounded hover:bg-red-50 text-[#6B7280] hover:text-red-600"
                                title="Delete user"
                                onClick={() => { setDeleteItem(item); setDeleteMode("deactivate"); }}
                                data-testid={`delete-${item.email}`}
                              >
                                <Trash size={14} />
                              </button>
                            </>
                          )}
                          {/* Resend/Revoke/Copy for pending invitations */}
                          {item.invite_status === "pending" && (
                            <>
                              <button className="p-1.5 rounded hover:bg-blue-50 text-[#6B7280] hover:text-blue-600" title="Copy link"
                                onClick={() => { if (item.token) { navigator.clipboard.writeText(`${window.location.origin}/onboarding/${item.token}`); toast.success(`Link copied for ${item.name}`); } else { toast.error("Token not available"); } }}>
                                <Link size={14} />
                              </button>
                              <button className="p-1.5 rounded hover:bg-[#F1F5F9] text-[#6B7280] hover:text-[#0F172A]" title="Resend" onClick={() => handleResend(item.id || "")}>
                                <ArrowClockwise size={14} />
                              </button>
                              <button className="p-1.5 rounded hover:bg-red-50 text-[#6B7280] hover:text-red-600" title="Revoke" onClick={() => handleRevoke(item.id || "")}>
                                <Trash size={14} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {(!dashboard.items || dashboard.items.length === 0) && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-[#9CA3AF]">No users or invitations yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Single Invite Modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6" data-testid="invite-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><UserPlus size={20} className="text-[#C5A059]" /> Invite New User</h3>
              <button onClick={() => { setShowInvite(false); setInviteResult(null); }}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            {inviteResult ? (
              <div className="space-y-4" data-testid="invite-result">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <WarningCircle size={20} className="text-amber-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-amber-800">{inviteResult.message}</p>
                      {inviteResult.action_required === "verify_domain" && <p className="text-xs text-amber-700 mt-1">Go to Settings &gt; Email Sending Domain to verify.</p>}
                    </div>
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-[#6B7280] mb-1 block">Onboarding Link</Label>
                  <div className="flex items-center gap-2">
                    <input readOnly value={inviteResult.onboarding_link} className="flex-1 border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm bg-[#F9FAFB] font-mono text-[#374151]" data-testid="invite-link-input" />
                    <Button variant="outline" size="sm" className="shrink-0 gap-1" onClick={() => { navigator.clipboard.writeText(inviteResult.onboarding_link); toast.success("Copied!"); }}>
                      <Copy size={14} /> Copy
                    </Button>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button className="flex-1 bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F]" onClick={() => { setInviteResult(null); setInviteForm({ name: "", email: "", role: "opportunity_finder", team_id: "" }); }}>Invite Another</Button>
                  <Button variant="outline" onClick={() => { setShowInvite(false); setInviteResult(null); }}>Close</Button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSingleInvite} className="space-y-4">
                <div><Label>Full Name</Label><Input value={inviteForm.name} onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })} placeholder="John Smith" className="mt-1" data-testid="invite-name-input" /></div>
                <div><Label>Email</Label><Input type="email" value={inviteForm.email} onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })} placeholder="john@example.com" className="mt-1" data-testid="invite-email-input" /></div>
                <div><Label>Role</Label><Select value={inviteForm.role} onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}><SelectTrigger className="mt-1" data-testid="invite-role-select"><SelectValue /></SelectTrigger><SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
                <div><Label>Assign Team</Label><Select value={inviteForm.team_id} onValueChange={(v) => setInviteForm({ ...inviteForm, team_id: v })}><SelectTrigger className="mt-1" data-testid="invite-team-select"><SelectValue placeholder="Select team..." /></SelectTrigger><SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
                <Button type="submit" disabled={sending} className="w-full bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-semibold gap-2" data-testid="send-invite-btn">
                  <PaperPlaneTilt size={16} /> {sending ? "Sending..." : "Send Invitation"}
                </Button>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Bulk Invite Modal */}
      {showBulk && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 overflow-y-auto py-8">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4 p-6" data-testid="bulk-invite-modal">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><Users size={20} className="text-[#C5A059]" /> Bulk Invite</h3>
              <button onClick={() => { setShowBulk(false); setBulkResult(null); }}><X size={20} className="text-[#6B7280]" /></button>
            </div>
            {bulkResult ? (
              <div className="space-y-4" data-testid="bulk-result">
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <WarningCircle size={20} className="text-amber-600 inline mr-1" />
                  <span className="text-sm font-medium text-amber-800">{bulkResult.message}</span>
                </div>
                <div className="max-h-60 overflow-y-auto space-y-2">
                  {(bulkResult.results || []).filter(r => r.status === "created").map((r, i) => (
                    <div key={i} className="bg-[#F9FAFB] rounded-lg px-3 py-2 flex items-center justify-between gap-2">
                      <div className="min-w-0"><div className="text-sm font-medium text-[#0F172A] truncate">{r.name}</div><div className="text-xs text-[#6B7280] truncate">{r.email}</div></div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        {r.email_sent ? <span className="text-xs text-green-600 font-medium flex items-center gap-1"><CheckCircle size={12} /> Sent</span> : (
                          <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={() => { navigator.clipboard.writeText(r.onboarding_link); toast.success(`Copied for ${r.name}`); }}>
                            <Copy size={12} /> Copy Link
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                <Button variant="outline" className="w-full" onClick={() => { setShowBulk(false); setBulkResult(null); setBulkText(""); }}>Close</Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Default Role</Label><Select value={bulkRole} onValueChange={setBulkRole}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select></div>
                  <div><Label>Default Team</Label><Select value={bulkTeam} onValueChange={setBulkTeam}><SelectTrigger className="mt-1"><SelectValue placeholder="Select team..." /></SelectTrigger><SelectContent>{teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select></div>
                </div>
                <div>
                  <Label>Paste users (one per line): <span className="text-[#6B7280] font-normal">Name, Email</span></Label>
                  <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} placeholder={"John Smith, john@example.com\nJane Doe, jane@example.com"} rows={8} className="mt-1 w-full border border-[#D1D5DB] rounded-lg px-3 py-2 text-sm focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059] outline-none resize-none" data-testid="bulk-text-input" />
                </div>
                <Button onClick={handleBulkInvite} disabled={sending} className="w-full bg-[#C5A059] text-[#0F172A] hover:bg-[#B8933F] font-semibold gap-2" data-testid="send-bulk-invite-btn">
                  <PaperPlaneTilt size={16} /> {sending ? "Sending..." : "Send All Invitations"}
                </Button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit User Dialog */}
      <Dialog open={!!editItem} onOpenChange={() => setEditItem(null)}>
        <DialogContent className="max-w-md" data-testid="edit-onboarding-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Edit User — {editItem?.name}</DialogTitle>
            <DialogDescription>Modify user details and onboarding status</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div><label className="text-xs font-semibold text-[#374151] mb-1 block">Name</label><Input value={editForm.name || ""} onChange={e => setEditForm({...editForm, name: e.target.value})} data-testid="ob-edit-name" /></div>
            <div><label className="text-xs font-semibold text-[#374151] mb-1 block">Email</label><Input value={editForm.email || ""} onChange={e => setEditForm({...editForm, email: e.target.value})} data-testid="ob-edit-email" /></div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Role</label>
              <select value={editForm.role || ""} onChange={e => setEditForm({...editForm, role: e.target.value})} className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm bg-white" data-testid="ob-edit-role">
                {ROLE_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Team</label>
              <select value={editForm.team_id || ""} onChange={e => setEditForm({...editForm, team_id: e.target.value})} className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm bg-white" data-testid="ob-edit-team">
                <option value="">No team</option>
                {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#374151] mb-1 block">Onboarding Status</label>
              <select value={editForm.onboarding_status || ""} onChange={e => setEditForm({...editForm, onboarding_status: e.target.value})} className="w-full h-10 px-3 rounded-md border border-[#E5E7EB] text-sm bg-white" data-testid="ob-edit-status">
                {ONBOARDING_STATUSES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button onClick={saveEdit} className="bg-[#0F172A] text-white hover:bg-[#1E293B]" data-testid="ob-save-edit-btn">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={!!deleteItem} onOpenChange={() => setDeleteItem(null)}>
        <DialogContent className="max-w-md" data-testid="delete-onboarding-user-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#0F172A]">Remove User</DialogTitle>
            <DialogDescription>Choose how to remove <strong>{deleteItem?.name}</strong></DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <button onClick={() => setDeleteMode("deactivate")} className={`w-full p-3 rounded-lg border text-left transition ${deleteMode === "deactivate" ? "border-[#C5A059] bg-[#FFFBEB]" : "border-[#E5E7EB] hover:bg-[#FAFAFA]"}`}>
              <p className="text-sm font-semibold text-[#0F172A]">Deactivate</p>
              <p className="text-xs text-[#6B7280] mt-0.5">Disable login but keep records. Reactivate later.</p>
            </button>
            <button onClick={() => setDeleteMode("permanent")} className={`w-full p-3 rounded-lg border text-left transition ${deleteMode === "permanent" ? "border-red-400 bg-red-50" : "border-[#E5E7EB] hover:bg-[#FAFAFA]"}`}>
              <p className="text-sm font-semibold text-red-700">Permanently Delete</p>
              <p className="text-xs text-[#6B7280] mt-0.5">Remove user and all data. Cannot be undone.</p>
            </button>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteItem(null)}>Cancel</Button>
            <Button onClick={confirmDelete} className={deleteMode === "permanent" ? "bg-red-600 text-white hover:bg-red-700" : "bg-[#0F172A] text-white hover:bg-[#1E293B]"} data-testid="ob-confirm-delete-btn">
              {deleteMode === "permanent" ? "Permanently Delete" : "Deactivate User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <OnboardingDocumentsPanel />
    </div>
  );
}

const DOC_LABELS = { photo_id: "Photo ID", right_to_work: "Right to Work" };
const AGREEMENT_LABELS = { nda: "NDA", partner: "Partner Agreement", senior_recruitment_partner: "360 Senior Recruitment Partner Agreement" };
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—");

// Admin filing view over the documents the Hub already stores:
// uploaded ID / Right to Work files (id_rtw_documents) and agreements (agreements).
function OnboardingDocumentsPanel() {
  const [data, setData] = useState({ items: [], stats: {} });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewer, setViewer] = useState(null); // { url, name, type }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/onboarding/admin/documents${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load documents"); }
    setLoading(false);
  }, [search]);
  useEffect(() => { load(); }, [load]);

  const openBlob = async (url, name, download) => {
    try {
      const resp = await api.get(url, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      if (download) {
        const a = document.createElement("a"); a.href = blobUrl; a.download = name; a.click();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
      } else {
        setViewer({ url: blobUrl, name, type: resp.data.type });
      }
    } catch { toast.error("Could not open document"); }
  };

  const verify = async (doc, verified) => {
    try {
      await api.post(`/onboarding/id-rtw/verify/${doc.id}`, { verified, note: verified ? "Verified by Admin" : "Rejected by Admin" });
      toast.success(verified ? "Document verified" : "Document rejected");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="bg-white rounded-lg border border-[#E5E7EB] p-4 space-y-3" data-testid="onboarding-documents-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-[#0F172A] flex items-center gap-2"><FileText size={20} /> Onboarding Documents</h2>
          <p className="text-xs text-[#6B7280]">ID, Right to Work and signed agreements for every user. Every document an admin opens is recorded in the Activity Log.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#6B7280]">{data.stats?.id_documents || 0} ID/RTW files · {data.stats?.signed_agreements || 0}/{data.stats?.agreements || 0} agreements signed</span>
          <Input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} className="h-8 w-56 text-xs" data-testid="documents-search" />
        </div>
      </div>
      {loading ? <div className="text-sm text-[#9CA3AF]">Loading…</div> : data.items.length === 0 ? (
        <div className="text-sm text-[#9CA3AF]">No onboarding documents found.</div>
      ) : (
        <div className="divide-y divide-[#F3F4F6]">
          {data.items.map(u => (
            <div key={u.user_id} className="py-3" data-testid={`documents-user-${u.user_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <span className="font-semibold text-sm text-[#0F172A]">{u.name}</span>
                <span className="text-xs text-[#6B7280]">{u.email}</span>
                <span className="text-[10px] px-2 py-0.5 rounded bg-slate-100 text-slate-700 capitalize">{(u.role || "").replace(/_/g, " ")}</span>
                {u.id_verified_externally && <span className="text-[10px] px-2 py-0.5 rounded bg-green-100 text-green-800">ID previously verified by Banks Morgan</span>}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {u.id_documents.map(d => (
                  <div key={d.id} className="flex items-center gap-2 border rounded-lg px-3 py-2 text-xs">
                    <FileText size={16} className="text-[#C5A059] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#0F172A]">{DOC_LABELS[d.doc_type] || d.doc_type}</div>
                      <div className="text-[#6B7280] truncate">{d.filename} · uploaded {fmtDate(d.uploaded_at)}</div>
                      <div className={d.status === "verified" ? "text-green-700" : d.status === "rejected" ? "text-red-600" : "text-amber-700"}>{d.status}{d.verified_note ? ` — ${d.verified_note}` : ""}</div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openBlob(`/onboarding/id-rtw/document/${d.id}/file`, d.filename)} data-testid={`view-doc-${d.id}`}><Eye size={14} /></Button>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openBlob(`/onboarding/id-rtw/document/${d.id}/file?download=1`, d.filename, true)}><DownloadSimple size={14} /></Button>
                    {d.status !== "verified" && <Button size="sm" className="h-7 px-2 bg-green-600 text-white hover:bg-green-700" onClick={() => verify(d, true)}>Verify</Button>}
                  </div>
                ))}
                {u.agreements.map(a => (
                  <div key={a.id} className="flex items-center gap-2 border rounded-lg px-3 py-2 text-xs">
                    <FileText size={16} className="text-[#0F172A] flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-[#0F172A]">{AGREEMENT_LABELS[a.agreement_type] || (a.agreement_type || "").replace(/_/g, " ")}</div>
                      <div className={a.status === "signed" ? "text-green-700" : "text-amber-700"}>
                        {a.status === "signed" ? `Signed by ${a.signature_name || u.name} · ${fmtDate(a.signed_at)}` : `Awaiting signature · issued ${fmtDate(a.created_at)}`}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openBlob(`/agreements/${a.id}/pdf`, `${a.agreement_type}.pdf`)} data-testid={`view-agreement-${a.id}`}><Eye size={14} /></Button>
                    <Button size="sm" variant="outline" className="h-7 px-2" onClick={() => openBlob(`/agreements/${a.id}/pdf`, `BM_${a.agreement_type}_${u.name.replace(/ /g, "_")}.pdf`, true)}><DownloadSimple size={14} /></Button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={!!viewer} onOpenChange={o => { if (!o) { if (viewer) URL.revokeObjectURL(viewer.url); setViewer(null); } }}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>{viewer?.name}</DialogTitle><DialogDescription>Confidential onboarding document</DialogDescription></DialogHeader>
          {viewer && (viewer.type?.startsWith("image/")
            ? <img src={viewer.url} alt={viewer.name} className="max-h-[70vh] mx-auto" data-testid="document-viewer-image" />
            : <iframe src={viewer.url} title={viewer.name} className="w-full h-[70vh] border rounded" data-testid="document-viewer-frame" />)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
